import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Bell, X, ChevronRight, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationPopupModal = ({ onNavigate }) => {
  const [popups, setPopups] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(false);

  const fetchPopups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/popups`);
      if (res.data && res.data.length > 0) {
        setPopups(res.data);
        setCurrentIdx(0);
        setVisible(true);
      }
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    // Check for popups on mount and every 60s
    fetchPopups();
    const interval = setInterval(fetchPopups, 60000);
    return () => clearInterval(interval);
  }, [fetchPopups]);

  const dismissCurrent = async () => {
    const current = popups[currentIdx];
    if (current) {
      try {
        await axios.patch(`${API}/notifications/${current.id}/read`);
      } catch (err) { /* silent */ }
    }
    if (currentIdx < popups.length - 1) {
      setCurrentIdx((i) => i + 1);
    } else {
      setVisible(false);
      setPopups([]);
    }
  };

  const dismissAll = async () => {
    for (const p of popups) {
      try {
        await axios.patch(`${API}/notifications/${p.id}/read`);
      } catch (err) { /* silent */ }
    }
    setVisible(false);
    setPopups([]);
  };

  const handleAction = (notif) => {
    if (notif.link_panel && onNavigate) {
      onNavigate(notif.link_panel, notif.product_id);
    }
    dismissCurrent();
  };

  if (!visible || popups.length === 0) return null;

  const current = popups[currentIdx];
  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="notification-popup-overlay">
      <div className="w-full max-w-md mx-4 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-testid="notification-popup-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Notification</h3>
              {popups.length > 1 && (
                <p className="text-white/70 text-xs">{currentIdx + 1} of {popups.length}</p>
              )}
            </div>
          </div>
          <button onClick={dismissAll} className="text-white/60 hover:text-white transition-colors" data-testid="popup-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1" data-testid="popup-title">{current.title}</h4>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed" data-testid="popup-message">{current.message}</p>
            </div>
          </div>

          {current.product_name && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/50 text-xs text-slate-500 dark:text-slate-400">
              Product: <span className="font-semibold text-slate-700 dark:text-slate-200">{current.product_name}</span>
            </div>
          )}

          <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-4">
            {new Date(current.created_at).toLocaleString()}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center gap-3">
          {current.link_panel && (
            <Button
              onClick={() => handleAction(current)}
              className="flex-1 bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold"
              data-testid="popup-action-btn"
            >
              Go to {current.link_panel === "goals" ? "Goals" : current.link_panel === "milestones" ? "Milestones" : current.link_panel === "painpoints" ? "Pain Points" : "Tasks"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          <Button
            variant="outline"
            onClick={dismissCurrent}
            className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
            data-testid="popup-dismiss-btn"
          >
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
};
